"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ncoToDlp = void 0;
const transformerUtils_1 = require("./transformerUtils");
const NcoVariableTree_1 = require("../NcoVariableTree");
const STATIC_DEPTH_VARIABLE_KEY = 'dep';
const allowedDlpAttributes = [
    'parameter_label',
    'parameter_name',
    'units',
    'format',
    'instrument_nr',
    'recording_interval',
    'measurement_depth',
    'measurement_height',
    'direction_sense',
    'magnetic_variation',
    'other_reference',
    'other_reference_offset',
    'reference_datum',
    'scaling_factor',
    'column',
    'comment',
    'longitude',
    'latitude',
    'definition',
    'direction_reference',
    'reference_time',
    'water_depth',
];
const createHeaderSection = (sectionName, attributes, isParameterSection) => {
    const filteredAttributes = Object.keys(attributes).reduce((prev, attrName) => {
        if (allowedDlpAttributes.includes(attrName) || attributes[attrName] === '') {
            return `${prev}${attrName.padEnd(35)}${attributes[attrName]}\n`;
        }
        else {
            return prev;
        }
    }, '');
    const commentAttribute = isParameterSection && !attributes['parameter_label']
        ? `${'comment'.padEnd(35)}Unrecognized parameter ${attributes['standard_name'] || attributes['short_name'] || attributes['long_name'] || ''}\n`
        : '';
    return `[${sectionName}]\n${commentAttribute}${filteredAttributes}\n`;
};
const numberToFilenameFriendlyFormat = (num) => {
    return num.toString().replace('-', 'neg');
};
function deepEqual(a, b) {
    if (typeof a === 'object' && a !== null && typeof b === 'object' && b !== null) {
        let count = [0, 0];
        for (var key in a)
            count[0]++;
        for (var key in b)
            count[1]++;
        if (count[0] - count[1] !== 0) {
            return false;
        }
        for (var key in a) {
            if (!(key in b) || !deepEqual(a[key], b[key])) {
                return false;
            }
        }
        for (var key in b) {
            if (!(key in a) || !deepEqual(b[key], a[key])) {
                return false;
            }
        }
        return true;
    }
    else {
        return a === b;
    }
}
exports.ncoToDlp = (ncoJson, options = {}) => {
    const filenamePrefix = !!options.filenamePrefix ? `${options.filenamePrefix}_` : '';
    const skipHeaders = !!options.skipHeaders;
    const DLP_COLUMN_PADDING = 21;
    const { dateDimensionKey, latDimensionKey, lonDimensionKey, depthDimensionKey, levelDimensionKey } = transformerUtils_1.identifyKeyDimensions(ncoJson);
    const lonVariable = {
        ...ncoJson.variables[lonDimensionKey],
        data: Array.isArray(ncoJson.variables[lonDimensionKey].data) ? ncoJson.variables[lonDimensionKey].data : [ncoJson.variables[lonDimensionKey].data],
    };
    const latVariable = {
        ...ncoJson.variables[latDimensionKey],
        data: Array.isArray(ncoJson.variables[latDimensionKey].data) ? ncoJson.variables[latDimensionKey].data : [ncoJson.variables[latDimensionKey].data],
    };
    const files = [];
    lonVariable.data.forEach((longitude, longitudeIndex) => {
        latVariable.data.forEach((latitude, latitudeIndex) => {
            const variablesSharingSameShape = Object.values(transformerUtils_1.getVariablesSharingSameShape(ncoJson)).filter((variablesWithSameShape) => {
                const shape = ncoJson.variables[variablesWithSameShape[0]].shape;
                const shouldInclude = shape.includes(dateDimensionKey) && shape.includes(lonDimensionKey) && shape.includes(latDimensionKey);
                const hasLevelOrDepthDimension = (depthDimensionKey && shape.includes(depthDimensionKey)) || (levelDimensionKey && shape.includes(levelDimensionKey));
                if (shouldInclude && ((hasLevelOrDepthDimension && shape.length === 4) || shape.length === 3)) {
                    return true;
                }
                else {
                    return false;
                }
            });
            if (variablesSharingSameShape.length === 0) {
                return;
            }
            const variables = variablesSharingSameShape.sort((a, b) => b.length - a.length)[0].filter((variable) => variable !== STATIC_DEPTH_VARIABLE_KEY);
            const { columns, rows } = transformerUtils_1.getDataFromVariablesSharingSameShapeDuplicateColumns({
                variables,
                ncoJson,
                ncoVariableTreeConfig: {
                    excludeDimensions: [
                        { dimensionKey: lonDimensionKey, inclusiveIndexRange: [longitudeIndex, longitudeIndex] },
                        { dimensionKey: latDimensionKey, inclusiveIndexRange: [latitudeIndex, latitudeIndex] },
                    ],
                },
                rowDimensionName: dateDimensionKey,
                order: options.order || 'ASC',
            });
            let waterDepth = null;
            if (ncoJson.variables[STATIC_DEPTH_VARIABLE_KEY] && ncoJson.variables[STATIC_DEPTH_VARIABLE_KEY].shape.length === 2) {
                const tree = new NcoVariableTree_1.NcoVariableTree(ncoJson, STATIC_DEPTH_VARIABLE_KEY, {
                    excludeDimensions: [
                        { dimensionKey: lonDimensionKey, inclusiveIndexRange: [longitudeIndex, longitudeIndex] },
                        { dimensionKey: latDimensionKey, inclusiveIndexRange: [latitudeIndex, latitudeIndex] },
                        { dimensionKey: 'time', inclusiveIndexRange: [0, 0] },
                    ],
                });
                waterDepth = tree.getData(tree.flattenData()[0]);
            }
            const dateDimensionIndex = columns.findIndex((column) => column.label === dateDimensionKey);
            const columnsWithoutADate = columns.filter((column) => column.label !== dateDimensionKey);
            const aggregatedColumnsWithRepeatingColumns = columnsWithoutADate.reduce((prevColumns, currentColumn, columnIndex) => {
                var _a, _b;
                const sameColumnIndex = prevColumns.findIndex(({ meta: { column, measurement_depth, ...prevColumnMeta }, ...prevColumn }) => {
                    var _a, _b, _c, _d;
                    return (prevColumn.label === currentColumn.label &&
                        deepEqual(prevColumnMeta, currentColumn.meta) &&
                        ((depthDimensionKey &&
                            ![null, undefined].includes((_a = currentColumn.atDimension) === null || _a === void 0 ? void 0 : _a[depthDimensionKey]) &&
                            ![null, undefined].includes((_b = prevColumn.atDimension) === null || _b === void 0 ? void 0 : _b[depthDimensionKey])) ||
                            (levelDimensionKey &&
                                ![null, undefined].includes((_c = currentColumn.atDimension) === null || _c === void 0 ? void 0 : _c[levelDimensionKey]) &&
                                ![null, undefined].includes((_d = prevColumn.atDimension) === null || _d === void 0 ? void 0 : _d[levelDimensionKey]))));
                });
                if (sameColumnIndex === -1) {
                    const column = {
                        ...currentColumn,
                        meta: {
                            ...currentColumn.meta,
                            ...(depthDimensionKey && ((_a = currentColumn.atDimension) === null || _a === void 0 ? void 0 : _a[depthDimensionKey]) !== undefined
                                ? { measurement_depth: currentColumn.atDimension[depthDimensionKey] }
                                : {}),
                            ...(levelDimensionKey && ((_b = currentColumn.atDimension) === null || _b === void 0 ? void 0 : _b[levelDimensionKey]) !== undefined
                                ? { measurement_height: currentColumn.atDimension[levelDimensionKey] }
                                : {}),
                            column: columnIndex + 2,
                        },
                    };
                    return [...prevColumns, column];
                }
                else {
                    const sameColumn = prevColumns[sameColumnIndex];
                    prevColumns[sameColumnIndex] = {
                        ...sameColumn,
                        meta: {
                            ...sameColumn.meta,
                            ...(sameColumn.meta.measurement_depth && depthDimensionKey && currentColumn.atDimension
                                ? { measurement_depth: `${sameColumn.meta.measurement_depth} ${currentColumn.atDimension[depthDimensionKey]}` }
                                : {}),
                            ...(sameColumn.meta.measurement_height && levelDimensionKey && currentColumn.atDimension
                                ? { measurement_height: `${sameColumn.meta.measurement_height} ${currentColumn.atDimension[levelDimensionKey]}` }
                                : {}),
                            column: `${sameColumn.meta.column} ${columnIndex + 2}`,
                        },
                    };
                    return prevColumns;
                }
            }, []);
            const { groupedColumns, nonGroupedColumns } = aggregatedColumnsWithRepeatingColumns.reduce((prev, currentColumn) => {
                if (currentColumn.meta.DLP_group_parameter) {
                    return {
                        ...prev,
                        groupedColumns: {
                            [currentColumn.meta.DLP_group_parameter]: [
                                ...(prev.groupedColumns[currentColumn.meta.DLP_group_parameter] ? prev.groupedColumns[currentColumn.meta.DLP_group_parameter] : []),
                                currentColumn,
                            ],
                        },
                    };
                }
                else {
                    return {
                        ...prev,
                        nonGroupedColumns: [...prev.nonGroupedColumns, currentColumn],
                    };
                }
            }, {
                nonGroupedColumns: [],
                groupedColumns: {},
            });
            const DLPHeaderSections = [
                createHeaderSection('location', { longitude, latitude, ...(typeof waterDepth === 'number' ? { water_depth: waterDepth } : {}) }, false),
                createHeaderSection('date', { column: 1, format: 'yyyy-mm-dd HH:MM:SS', reference_time: 'UTC +00:00' }, false),
                ...nonGroupedColumns.map((header, i) => createHeaderSection(`parameter${i + 1}:${header.meta.parameter_name || header.label}`, { ...header.meta, format: '0.4f' }, true)),
                ...Object.keys(groupedColumns).map((DLP_group_parameter, i) => {
                    const columnNumber = nonGroupedColumns.length + 1 + i;
                    const columnsSharingGroupParameter = groupedColumns[DLP_group_parameter];
                    const groupParameterAttributes = Object.keys(columnsSharingGroupParameter[0].meta).reduce((prev, attributeKey) => {
                        if (attributeKey === 'measurement_depth' || attributeKey === 'measurement_height') {
                            return {
                                ...prev,
                                [attributeKey]: columnsSharingGroupParameter[0].meta[attributeKey],
                            };
                        }
                        else {
                            return prev;
                        }
                    }, {});
                    let groupedHeaderSections = [];
                    groupedHeaderSections.push(createHeaderSection(`parameter${columnNumber}.0:${DLP_group_parameter}`, groupParameterAttributes, false));
                    columnsSharingGroupParameter.forEach((columnSharingGroupParameter, index) => {
                        const { measurement_depth, measurement_height, ...attributesWithoutLevel } = columnSharingGroupParameter.meta;
                        groupedHeaderSections.push(createHeaderSection(`parameter${columnNumber}.${index + 1}:${columnSharingGroupParameter.meta.parameter_name || columnSharingGroupParameter.label}`, { ...attributesWithoutLevel, format: '0.4f' }, true));
                    });
                    return groupedHeaderSections.join('');
                }),
            ];
            const DLPDataRows = rows.map((row) => {
                var _a;
                const firstDateColumn = `${(_a = row[dateDimensionIndex]) === null || _a === void 0 ? void 0 : _a.toString().replace('T', ' ').slice(0, 19)},`.padEnd(DLP_COLUMN_PADDING);
                return (firstDateColumn +
                    row
                        .map((column, i) => {
                        if (i === dateDimensionIndex) {
                            return '';
                        }
                        else {
                            const columnValue = column === null ? 'NaN' : column.toString();
                            const columnSuffix = i === row.length - 1 ? '' : ',';
                            return `${columnValue}${columnSuffix}`.padEnd(DLP_COLUMN_PADDING);
                        }
                    })
                        .join(''));
            });
            const columnDelimiter = '%|';
            const dataColumn = '[data]';
            let dataSection = '';
            dataSection += `${dataColumn}\n`;
            dataSection += columnDelimiter.padEnd(DLP_COLUMN_PADDING - (columnDelimiter.length + dataColumn.length));
            dataSection += 'date'.padEnd(columnDelimiter.length + dataColumn.length);
            dataSection += rows[0]
                .slice(0, rows[0].length - 1)
                .map((_, i) => (i + 1 + 1).toString().padEnd(DLP_COLUMN_PADDING))
                .join('');
            dataSection += `\n${columnDelimiter}\n`;
            const file = {
                filename: `${filenamePrefix}${numberToFilenameFriendlyFormat(longitude)}_${numberToFilenameFriendlyFormat(latitude)}.dlp`,
                fileContent: skipHeaders ? DLPDataRows.join('\n') : DLPHeaderSections.join('') + dataSection + DLPDataRows.join('\n'),
            };
            files.push(file);
        });
    });
    return files;
};
